import { ReactNode, useCallback, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { RouterTextLink } from "components/actions/RouterLink";
import {
  ChannelIcon,
  ContactChannelList,
  DOTTED_UNDERLINE,
} from "components/contacts/contact_channels/ContactChannelList";
import { visibleChannels } from "components/contacts/contact_channels/contactChannels";
import { InlineSaveLeadButton } from "components/leads/saving/InlineSaveLeadButton";
import { SaveLeadDrawerProvider } from "components/leads/saving/SaveLeadDrawerProvider";
import { OutreachBuilderModal } from "components/outreach_builder/OutreachBuilderModal";
import BuyerCategoriesWithPopover from "components/table_components/BuyerCategoriesWithPopover";
import Button from "components/ui/button/Button";
import CursorPagination from "components/ui/cursor_pagination/CursorPagination";
import EmptyState from "components/ui/empty_state/EmptyState";
import toast from "components/ui/toast/Toast";
import EllipsisText from "components/ui/tooltip/EllipsisText";
import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import Tooltip from "components/ui/tooltip/Tooltip";
import { appRoutes } from "lib/appRoutes";
import { HOVER_CONTAINER, SHOW_ON_HOVER } from "lib/core_components/ShowOnHover";
import SkeletonTable from "lib/core_components/SkeletonTable";
import { Table } from "lib/core_components/Table";
import { ColumnSizeConfig, ColumnType } from "lib/core_components/Table/ColumnTypes";
import { tableRowSelection } from "lib/core_components/Table/Table";
import { ContactSearchResult } from "lib/hooks/api/contacts/useContactSearch";
import { useRevealContact, useRevealContacts } from "lib/hooks/api/contacts/useRevealContacts";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventNames, useTracking } from "lib/tracking";
import { CognismContactSource, RevealStatus } from "lib/types/graphQLEnums";
import { BuyerBadge } from "../ContactsTableUtils";
import {
  contactRevealRef,
  contactRevealState,
  contactSaveEntity,
  UnifiedContactRow,
} from "./contactSource";
import { contactEventData, revealEventData } from "./contactTracking";
import SendCognismContactToCRMButton from "./SendCognismContactToCRMButton";

// The design gives every column a flat 240px at every breakpoint, with the name column — which
// also carries the row's hover actions — wider at 320px.
const DEFAULT_SIZE: ColumnSizeConfig = { small: 240, medium: 240, large: 240, xlarge: 240 };
const NAME_SIZE: ColumnSizeConfig = { small: 320, medium: 320, large: 320, xlarge: 320 };

/**
 * Columns an embedding surface can drop. Buyer and Buyer type are redundant under a locked
 * single-buyer scope (every row carries the same values), so the buyer profile hides both.
 */
export type HideableContactColumn = "buyer" | "buyerType";

// Stable default so the columns memo isn't busted by a fresh [] every render.
const NO_HIDDEN_COLUMNS: HideableContactColumn[] = [];

export type CognismContactsTableViewProps = {
  contacts: UnifiedContactRow[];
  /** Page size from the container; sizes the loading skeleton to match the query. */
  pageSize: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  totalResults: number | null;
  hasNextPage: boolean;
  canGoPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  /** Composite `source:nativeId` keys of the selected rows (owned by the container). */
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  /** Tier key → display label, from the backend-served options (see useSeniorityTierOptions). */
  seniorityTierLabels: Record<string, string>;
  /** Rendered under the zero-results empty state (e.g. a request-contacts CTA). */
  emptyStateAction?: ReactNode;
  /** Columns to omit — see {@link HideableContactColumn}. */
  hiddenColumns?: HideableContactColumn[];
};

/** The contact's name, or "" when there isn't one — for forms and prompts, where a
 * "Unknown contact" placeholder would end up in the generated copy. */
function contactName(contact: ContactSearchResult): string {
  return contact.fullName || [contact.firstName, contact.lastName].filter(Boolean).join(" ");
}

function displayName(contact: ContactSearchResult): string {
  return contactName(contact) || "Unknown contact";
}

/** The channels a reveal came back with, whichever mutation answered. */
type RevealedChannels = {
  email?: string | null;
  mobilePhoneNumbers?: (string | null)[] | null;
  directPhoneNumbers?: (string | null)[] | null;
  phoneNumbers?: (string | null)[] | null;
};

/** The organisation to attribute a contact to, whichever source it came from. */
function contactOrgName(contact: ContactSearchResult): string {
  return contact.buyer?.name ?? contact.account?.name ?? contact.oscarOrgName ?? "";
}

// Whether the Contacts cell stacks more than one line, making the row taller than a single
// line of text. Resting rows are one line of glyphs; "Unavailable" and "Not found" are one
// line of text. Drives the row's vertical alignment — see EXPANDED_ROW_CLASS.
function isExpandedRow(contact: ContactSearchResult, revealing: boolean): boolean {
  const state = contactRevealState(contact);
  if (state === "unavailable") return false;
  const revealed = state === "revealed";
  if (!revealed && !revealing) return false;
  return visibleChannels(contact, revealed).length > 1;
}

// The reveal eye. Hidden with opacity (not display:none) so it stays in the tab order
// and reachable by keyboard/touch: shown on row hover, on focus, and always on coarse
// pointers where hover never fires. It claims no width until visible, so the value only
// truncates once the button is actually there to make room for. The gap lives here
// rather than on the row, so it costs nothing while hidden.
const revealEyeVisible = { opacity: 1, width: "auto", overflow: "visible", marginLeft: 8 };
const RevealEyeSlot = styled.span({
  flexShrink: 0,
  opacity: 0,
  width: 0,
  overflow: "hidden",
  transition: "opacity 0.1s ease",
  "&:focus-within": revealEyeVisible,
  [`.${HOVER_CONTAINER}:hover &`]: revealEyeVisible,
  "@media (hover: none)": revealEyeVisible,
});

// Tooltip on the resting cell. The whole cell is the target, so it names the gesture.
const REVEAL_HINT = "Click to reveal";

// The merged Contacts cell: phone, email and LinkedIn in one column.
//
// Resting, it is a row of muted glyphs — one per channel actually on file — and the
// whole cell reveals on click, with the eye as the explicit affordance. Revealed, the
// found channels stack one per line in the same order. `unavailable` (owned but PII
// hydration failed) shows a muted notice; nothing on file at all shows "Not found".
function ContactsCell({
  contact,
  revealing,
  onReveal,
}: {
  contact: ContactSearchResult;
  revealing: boolean;
  onReveal: () => void;
}) {
  const state = contactRevealState(contact);
  if (state === "unavailable") {
    return <Text color="sysTextSecondary">Unavailable</Text>;
  }

  const revealed = state === "revealed";
  const visibleRows = visibleChannels(contact, revealed);

  if (visibleRows.length === 0) {
    return <Text color="sysTextSecondary">Not found</Text>;
  }

  // Revealing or revealed: one line per found channel, phone → email → LinkedIn. The lines
  // themselves are shared with the lead's contact cards — see ContactChannelList.
  if (revealed || revealing) {
    return (
      <ContactChannelList
        rows={visibleRows}
        trackingData={contactEventData(contact)}
        revealing={revealing}
      />
    );
  }

  // Resting: muted glyphs side by side, whole cell clickable. Mouse-only convenience —
  // the eye stays the keyboard/AT path, so the cell isn't turned into a nested button.
  return (
    <Tooltip content={REVEAL_HINT}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        minWidth={0}
        onClick={onReveal}
        style={{ cursor: "pointer" }}
      >
        <Flex alignItems="center" gap={12} minWidth={0} className="masked-detail">
          {visibleRows.map((row) => (
            <ChannelIcon key={row.key} role="img" aria-label={row.ariaLabel ?? row.key}>
              <UIcon icon={row.icon} size={16} color="sysTextPlaceholder" />
            </ChannelIcon>
          ))}
        </Flex>
        <RevealEyeSlot>
          <Button
            buttonType="secondary"
            icon="eye"
            aria-label="Reveal contact details"
            // The cell is clickable too — don't bubble into a second reveal. A duplicate
            // redeem costs Stotles twice for one user intent.
            onClick={(e) => {
              e.stopPropagation();
              onReveal();
            }}
          />
        </RevealEyeSlot>
      </Flex>
    </Tooltip>
  );
}

// Buyer name: a quiet in-row link, Body-M regular rather than the 600 the shared BuyerDetailsCell
// uses for a buyer heading. Truncation is the anchor's own `ellipsis` (nowrap + text-overflow) —
// the shared cell clamps with `-webkit-box` instead, which drops the whole overflowing word and so
// broke a long buyer name off well short of the column edge. `minWidth: 0` lets the anchor shrink
// below its text inside the flex row; without it the name overflows rather than truncating.
const BuyerLinkCell = styled(Flex)(({ theme }) => ({
  a: {
    ...DOTTED_UNDERLINE,
    minWidth: 0,
    color: theme.colors.sysTextSecondary,
    fontWeight: 400,
    lineHeight: "22px",
  },
  "a:hover": { ...DOTTED_UNDERLINE, color: theme.colors.sysTextDefault },
}));

function LinkedBuyerCell({ buyer }: { buyer: NonNullable<ContactSearchResult["buyer"]> }) {
  return (
    <BuyerLinkCell alignItems="center" minWidth={0}>
      <BuyerBadge url={buyer.logoUrl} />
      <EllipsisTooltip fullText={buyer.name}>
        {(ref) => (
          <RouterTextLink ref={ref} ellipsis {...appRoutes.buyerDetail(buyer.id)}>
            {buyer.name}
          </RouterTextLink>
        )}
      </EllipsisTooltip>
    </BuyerLinkCell>
  );
}

// Buyer cell for a contact with no linked buyer: Oscar's `oscarOrgName` (most Oscar contacts
// aren't tracked buyers) or Cognism's account name. Carries the same badge-left-of-name layout
// as a linked buyer, on the placeholder badge — an untracked org has no logo on file. With no
// name at all there is no buyer to badge, so the dash stands alone.
function UnlinkedBuyerCell({ name }: { name: string | null }) {
  if (!name) {
    return <EllipsisText>—</EllipsisText>;
  }
  return (
    <Flex alignItems="center" minWidth={0}>
      <BuyerBadge />
      {/* Same secondary weight and colour as a linked buyer, minus the link's underline. */}
      <EllipsisText color="sysTextSecondary">{name}</EllipsisText>
    </Flex>
  );
}

// Buyer-type column: the detailed "Parent > Category" assignments (with an "N more"
// popover) — moved here off the Buyer column, which now shows the buyer name only.
function BuyerTypeCell({ contact }: { contact: ContactSearchResult }) {
  const assignments = contact.buyer?.categoryAssignments ?? [];
  if (assignments.length === 0) {
    return <EllipsisText color="sysTextSecondary">—</EllipsisText>;
  }
  return (
    <BuyerCategoriesWithPopover
      size="default"
      buyerCategories={assignments.map((c) => ({
        buyer_category_id: c.categoryId,
        source: c.source,
      }))}
      buyerName={contact.buyer?.name ?? ""}
    />
  );
}

// The hover actions are 32px buttons against a 22px line of text. Left in the line's height they
// grow the row on hover, and a centred line re-centres — nudging the name ~5px down, which reads
// as a jump once the row is top-aligned. Pinning the line to the text's own height holds the name
// still and lets the taller buttons overflow into the cell's padding, as the design does.
const NameLine = styled(Flex)({ height: 22 });

type ColumnParams = {
  seniorityTierLabels: Record<string, string>;
  revealingKeys: Set<string>;
  onReveal: (contact: UnifiedContactRow) => void;
  onOutreach: (contact: UnifiedContactRow) => void;
  hiddenColumns: HideableContactColumn[];
};

function buildColumns({
  seniorityTierLabels,
  revealingKeys,
  onReveal,
  onOutreach,
  hiddenColumns,
}: ColumnParams): ColumnType<UnifiedContactRow>[] {
  const columns: ColumnType<UnifiedContactRow>[] = [
    {
      key: "contactName",
      title: "Contact name",
      sizeConfig: NAME_SIZE,
      ellipsis: true,
      render: (_value, contact) => {
        // Source-aware per-row save: Oscar by oscar_contacts.id; Cognism by redeemId
        // (redeem-on-save). A Cognism row with no redeemId isn't saveable.
        const saveEntity = contactSaveEntity(contact);
        return (
          <NameLine justifyContent="space-between" alignItems="center" gap={8} minWidth={0}>
            <Flex alignItems="center" gap={6} minWidth={0}>
              <EllipsisText fontWeight="500">{displayName(contact)}</EllipsisText>
            </Flex>
            <Flex alignItems="center" gap={4} style={{ flexShrink: 0 }}>
              {/* Save renders as an icon-only bookmark; it self-hides until hover when unsaved
                  and stays visible (filled) once saved. */}
              {saveEntity && (
                <InlineSaveLeadButton
                  entityType={saveEntity.entityType}
                  entityId={saveEntity.entityId}
                  savedEntityId={saveEntity.savedEntityId}
                  defaultValues={{ entities: [] }}
                  savedLeads={[]}
                  displayType="button"
                  buttonProps={{
                    buttonType: "secondary",
                    buttonText: "",
                    icon: { icon: "bookmark", color: "sysTextSecondary", fill: "sysTextSecondary" },
                  }}
                />
              )}
              {/* CRM push + outreach appear together on row hover. */}
              <span className={SHOW_ON_HOVER}>
                <Flex alignItems="center" gap={4}>
                  {/* Source-aware: Oscar sends directly (PII already loaded), Cognism redeems-on-push. */}
                  <SendCognismContactToCRMButton contact={contact} />
                  {/* Opens the outreach builder to draft a message rather than sending one. */}
                  <Tooltip content="Generate outreach">
                    <Button
                      buttonType="secondary"
                      icon="send"
                      iconProps={{ color: "sysTextSecondary" }}
                      aria-label="Generate outreach"
                      onClick={() => onOutreach(contact)}
                    />
                  </Tooltip>
                </Flex>
              </span>
            </Flex>
          </NameLine>
        );
      },
    },
    {
      key: "jobTitle",
      title: "Job title",
      sizeConfig: DEFAULT_SIZE,
      ellipsis: true,
      render: (_value, contact) => (
        <EllipsisText color="sysTextSecondary">{contact.jobTitle || "—"}</EllipsisText>
      ),
    },
    {
      key: "buyer",
      title: "Buyer",
      sizeConfig: DEFAULT_SIZE,
      ellipsis: true,
      render: (_value, contact) =>
        contact.buyer ? (
          <LinkedBuyerCell buyer={contact.buyer} />
        ) : (
          <UnlinkedBuyerCell name={contact.oscarOrgName ?? contact.account?.name ?? null} />
        ),
    },
    {
      key: "contacts",
      title: "Contacts",
      sizeConfig: DEFAULT_SIZE,
      render: (_value, contact) => (
        <ContactsCell
          contact={contact}
          revealing={revealingKeys.has(contact.selectionKey)}
          onReveal={() => onReveal(contact)}
        />
      ),
    },
    {
      key: "buyerType",
      title: "Buyer type",
      sizeConfig: DEFAULT_SIZE,
      ellipsis: true,
      render: (_value, contact) => <BuyerTypeCell contact={contact} />,
    },
    {
      key: "seniority",
      title: "Seniority",
      sizeConfig: DEFAULT_SIZE,
      ellipsis: true,
      // Unified seniority tier key resolved to its display label.
      render: (_value, contact) => (
        <EllipsisText color="sysTextSecondary">
          {contact.seniorityTier ? seniorityTierLabels[contact.seniorityTier] ?? "—" : "—"}
        </EllipsisText>
      ),
    },
    {
      key: "function",
      title: "Function",
      sizeConfig: DEFAULT_SIZE,
      ellipsis: true,
      // jobFunction is returned by search (both tiers), so it shows for every row.
      render: (_value, contact) => (
        <EllipsisText color="sysTextSecondary">
          {contact.jobFunction?.length ? contact.jobFunction.join(", ") : "—"}
        </EllipsisText>
      ),
    },
  ];

  const hidden = new Set<ColumnType<UnifiedContactRow>["key"]>(hiddenColumns);
  return columns.filter((column) => !hidden.has(column.key));
}

// Rows are vertically centred by default (antd leaves cells at the `vertical-align: middle`
// browser default). A revealed Contacts cell stacks one line per channel, and centring a tall
// row leaves every neighbouring column floating mid-row — so multi-line rows top-align instead,
// letting the stack grow downward off the first line. Single-line rows stay centred.
const EXPANDED_ROW_CLASS = "contacts-row-expanded";
const TableLayout = styled(Flex)({
  [`tr.${EXPANDED_ROW_CLASS} > td`]: { verticalAlign: "top" },
  // The checkbox is shorter than a line of text, so top-aligning it alone reads ~2px high.
  [`tr.${EXPANDED_ROW_CLASS} > td.ant5-table-selection-column .ant5-checkbox-wrapper`]: {
    marginTop: 2,
  },
});

/**
 * Presentational table for Cognism preview contacts. Data-source-agnostic so it
 * can be rendered in Storybook with mock rows; the container
 * (`CognismContactsTable`) owns fetching and cursor state.
 *
 * Contact details are masked until revealed: hovering a row surfaces an eye on the
 * phone/email cells; clicking either reveals both (one reveal returns all channels),
 * with a skeleton shimmer while in flight. Reuses the existing reveal mutation.
 */
function CognismContactsTableView({
  contacts,
  pageSize,
  isLoading,
  isFetching,
  isError,
  totalResults,
  hasNextPage,
  canGoPrevious,
  onNext,
  onPrevious,
  selectedKeys,
  onSelectionChange,
  seniorityTierLabels,
  emptyStateAction,
  hiddenColumns = NO_HIDDEN_COLUMNS,
}: CognismContactsTableViewProps) {
  const { mutate: revealContact } = useRevealContact();
  const { mutate: revealContacts } = useRevealContacts();
  const { logEvent } = useTracking();
  // Rows with a reveal in flight — drives the per-cell skeleton across both cells.
  const [revealingKeys, setRevealingKeys] = useState<Set<string>>(new Set());
  // The row the outreach builder is open for, if any.
  const [outreachContact, setOutreachContact] = useState<UnifiedContactRow | null>(null);

  const onReveal = useCallback(
    (contact: UnifiedContactRow) => {
      const ref = contactRevealRef(contact);
      if (contactRevealState(contact) !== "revealable" || !ref) return;
      const key = contact.selectionKey;
      setRevealingKeys((prev) => {
        if (prev.has(key)) return prev;
        return new Set(prev).add(key);
      });

      // A suppressed contact comes back REDEEMED with no channels, so without the status
      // this reported it as a genuine reveal — which is why an export calling the same
      // contact "unavailable" looked like it contradicted the table.
      const onRevealed = (status: RevealStatus, revealed: RevealedChannels | null) => {
        const suppressed = status === RevealStatus.Suppressed;
        logEvent(EventNames.contactsRevealed, {
          ...revealEventData("Row", {
            revealedCount: suppressed ? 0 : 1,
            suppressedCount: suppressed ? 1 : 0,
            failedCount: 0,
          }),
          "Has channels": !!(
            revealed?.email ||
            revealed?.mobilePhoneNumbers?.length ||
            revealed?.directPhoneNumbers?.length ||
            revealed?.phoneNumbers?.length
          ),
          ...contactEventData(contact),
        });
        // Names email and phone specifically: suppression withholds only those two, so a
        // suppressed contact can still have a LinkedIn URL, and the row will show it.
        // Information rather than an error — just not a reveal to claim credit for.
        if (suppressed) {
          toast.info(`Email and phone for ${displayName(contact)} are withheld (privacy).`);
          return;
        }
        toast.success(`Revealed ${displayName(contact)}.`);
      };
      const onError = () => {
        logEvent(EventNames.contactsRevealed, {
          ...revealEventData("Row", {
            revealedCount: 0,
            suppressedCount: 0,
            failedCount: 1,
          }),
          ...contactEventData(contact),
        });
        toast.error(`Couldn't reveal ${displayName(contact)}. Please try again.`);
      };
      const onSettled = () =>
        setRevealingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });

      // Cognism reveals by redeemId through the single mutation. Oscar has no redeemId, so it
      // goes through the source-tagged bulk mutation as a batch of one; its per-item status is
      // the outcome, and a PROVIDER_ERROR item is a failed reveal like a rejected request.
      if (contact.source === CognismContactSource.Oscar) {
        revealContacts(
          { input: { contacts: [ref] } },
          {
            onSuccess: (data) => {
              const item = data.revealContacts.perItem[0];
              if (!item || item.status === RevealStatus.ProviderError) return onError();
              onRevealed(item.status, item.contact ?? null);
            },
            onError,
            onSettled,
          },
        );
        return;
      }
      revealContact(
        { input: { redeemId: ref.id } },
        {
          onSuccess: (data) => onRevealed(data.revealContact.status, data.revealContact.contact),
          onError,
          onSettled,
        },
      );
    },
    [revealContact, revealContacts, logEvent],
  );

  // Outreach doesn't need a reveal: the builder writes from the name, job title and buyer,
  // all of which a preview row already carries. An unrevealed row just has no address to
  // pre-fill, so the modal drops its "To:" line and the message is copy-only.
  const onOutreach = useCallback(
    (contact: UnifiedContactRow) => {
      logEvent(EventNames.openOutreachModal, contactEventData(contact));
      setOutreachContact(contact);
    },
    [logEvent],
  );

  const columns = useMemo(
    () =>
      buildColumns({
        seniorityTierLabels,
        revealingKeys,
        onReveal,
        onOutreach,
        hiddenColumns,
      }),
    [seniorityTierLabels, revealingKeys, onReveal, onOutreach, hiddenColumns],
  );

  if (isLoading) {
    return <SkeletonTable columns={columns} pageSize={pageSize} rowCount={pageSize} active />;
  }

  if (!isError && contacts.length === 0) {
    return (
      <Flex justifyContent="center" width="100%">
        <EmptyState
          type="noResults"
          heading="No contacts found"
          description="We couldn't find any contacts for this search."
          action={emptyStateAction}
          yAxisMargin="64px"
        />
      </Flex>
    );
  }

  return (
    // SaveLeadDrawerProvider (APP-4677) powers the per-row InlineSaveLeadButton; the
    // table itself is the unified, source-aware one (selectionKey across both sources).
    <SaveLeadDrawerProvider trackingData={{ "Context source": "Cognism contacts in row" }}>
      <TableLayout column gap={16}>
        <Table<UnifiedContactRow>
          ariaLabel="Contacts table"
          columns={columns}
          dataSource={contacts}
          loading={isFetching}
          rowKey={(row) => row.selectionKey}
          rowClassName={(contact) =>
            isExpandedRow(contact, revealingKeys.has(contact.selectionKey))
              ? EXPANDED_ROW_CLASS
              : ""
          }
          rowSelection={tableRowSelection(
            selectedKeys,
            onSelectionChange,
            contacts,
            "selectionKey",
          )}
          pagination={false}
          isError={isError}
        />
        <CursorPagination
          hasNextPage={hasNextPage}
          canGoPrevious={canGoPrevious}
          onNext={onNext}
          onPrevious={onPrevious}
          isLoading={isFetching}
          rangeLabel={
            totalResults != null ? (
              <Text color="sysTextSecondary">{totalResults.toLocaleString()} contacts</Text>
            ) : undefined
          }
        />
      </TableLayout>
      <OutreachBuilderModal
        isOpen={!!outreachContact}
        onClose={() => setOutreachContact(null)}
        buyerName={outreachContact ? contactOrgName(outreachContact) : ""}
        contactEmail={outreachContact?.email ?? ""}
        contactName={outreachContact ? contactName(outreachContact) : ""}
        jobTitle={outreachContact?.jobTitle ?? ""}
      />
    </SaveLeadDrawerProvider>
  );
}

export default CognismContactsTableView;
